import { normalizeToIsoZ } from '../../utils/dateNormalization'
import {
  buildImportPreviewRecord,
  buildImportPreviewRows,
  buildImportSession,
  buildImportValidationResults,
  createImportDedupeIssue,
  createImportRowIssue,
  mapImportHeaders,
  parseCsvImportTable,
  type ImportMappedHeader,
  type ImportRecordResult,
  type ImportSession,
  type ImportSourceFile,
  type ParsedImportTable
} from '../../utils/importTable'
import { ensureUniqueImportedRowId, toStableImportIdString } from '../../utils/importRowIdentity'
import { findAvailableLotMatch, resolveCanonicalLotKey } from '../../utils/lotIdentity'
import { toDayKeyDateOnly } from '../../utils/productionFeedKgDerivation'

export type MortalityImportRecord = {
  id: string
  farmId: string
  productionUnitId: string
  recordedAt: string
  lot?: string
  lotId?: string
  mortalityCount: string
  notes?: string
  metadata?: Record<string, unknown>
}

export type MortalityImportLot = {
  id: string
  lotKey: string
  stage?: string
}

type MortalityImportTargetKey = 'mortality'
type MortalityImportContext = {
  farmId: string
  productionUnitId: string
}
type MortalityImportField = keyof MortalityImportRecord | 'lotId'
type MortalityMappedHeader = ImportMappedHeader<MortalityImportField>

export type MortalityImportRowResult = ImportRecordResult<MortalityImportRecord, MortalityImportField>
export type MortalityImportSession = ImportSession<
  MortalityImportRecord,
  MortalityImportField,
  MortalityImportTargetKey,
  MortalityImportContext
>

// Strip accent marks before lowercasing so Spanish-locale headers like
// "Mortalidad", "Notas" normalize to their accent-less forms and match
// aliases without listing every diacritic variant (mirrors the Water
// importer's #46 fix).
const MORTALITY_DIACRITIC_MARK_PATTERN = /[\u0300-\u036f]/g

const normalizeMortalityImportHeader = (value: string): string => (
  value
    .toString()
    .trim()
    .normalize('NFD')
    .replace(MORTALITY_DIACRITIC_MARK_PATTERN, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
)

const MORTALITY_IMPORT_FIELD_MAP: Record<string, MortalityImportField> = {
  id: 'id',
  farmid: 'farmId',
  productionunitid: 'productionUnitId',
  productionunit: 'productionUnitId',
  unitid: 'productionUnitId',
  date: 'recordedAt',
  datetime: 'recordedAt',
  recordedat: 'recordedAt',
  timestamp: 'recordedAt',
  time: 'recordedAt',
  lot: 'lot',
  lotid: 'lotId',
  mortality: 'mortalityCount',
  dailymortality: 'mortalityCount',
  mortalitycount: 'mortalityCount',
  deadcount: 'mortalityCount',
  losses: 'mortalityCount',
  morts: 'mortalityCount',
  notes: 'notes',
  note: 'notes',
  comment: 'notes',
  comments: 'notes',
  // Spanish-locale aliases.
  fecha: 'recordedAt',
  fechahora: 'recordedAt',
  lote: 'lot',
  loteid: 'lotId',
  mortalidad: 'mortalityCount',
  mortalidaddiaria: 'mortalityCount',
  mortandad: 'mortalityCount',
  bajas: 'mortalityCount',
  muertes: 'mortalityCount',
  notas: 'notes',
  nota: 'notes',
  observaciones: 'notes',
  observacion: 'notes',
  comentarios: 'notes',
  comentario: 'notes'
}

const trimOptionalString = (value: unknown): string | undefined => {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return undefined
    return value.toString()
  }
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed || undefined
}

const normalizeImportedMortalityCountString = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || !Number.isInteger(value) || value < 0) return undefined
    return value.toString()
  }
  if (typeof value !== 'string') return undefined

  const trimmed = value.trim()
  if (!trimmed) return undefined

  const parsed = Number.parseFloat(trimmed.replaceAll(',', '.'))
  if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed < 0) return undefined
  return parsed.toString()
}

type ResolvedImportedLot = {
  lotKey?: string
  // Charter D-1: MortalityLogService now rejects a create with no lotId.
  // Only set when resolved against a real Lot (an exact lotKeyById hit or a
  // matched AvailableLotMatch) — a free-text label or unrecognized id string
  // cannot be trusted as a UUID, so those cases leave lotId unset and
  // surface as a required-field error instead of silently shipping an
  // unsyncable row.
  lotId?: string
}

const resolveImportedMortalityLot = (args: {
  row: Record<string, unknown>
  availableLots: MortalityImportLot[]
  lotKeyById: Map<string, string>
}): ResolvedImportedLot => {
  const incomingLotId = trimOptionalString(args.row.lotId)
  const incomingLot = trimOptionalString(args.row.lot)

  if (incomingLotId) {
    const mappedLot = args.lotKeyById.get(incomingLotId)
    if (mappedLot) return { lotKey: mappedLot, lotId: incomingLotId }

    const matchedFromId = findAvailableLotMatch(incomingLotId, args.availableLots)
    if (matchedFromId.status === 'matched') {
      return { lotKey: matchedFromId.match.lotKey, lotId: matchedFromId.match.id }
    }

    if (!incomingLot) return { lotKey: incomingLotId }
  }

  if (!incomingLot) return {}

  const matchedFromLabel = findAvailableLotMatch(incomingLot, args.availableLots)
  if (matchedFromLabel.status === 'matched') {
    return { lotKey: matchedFromLabel.match.lotKey, lotId: matchedFromLabel.match.id }
  }

  return { lotKey: incomingLot }
}

const validateMortalityImportRecord = (args: {
  previewRow: Parameters<typeof createImportRowIssue<MortalityImportField>>[0]['previewRow']
  record: Record<string, unknown>
}) => {
  const issues: Array<ReturnType<typeof createImportRowIssue<MortalityImportField>>> = []
  const rawRecordedAt = trimOptionalString(args.record.recordedAt)
  const rawMortalityCount = trimOptionalString(args.record.mortalityCount)

  if (!rawRecordedAt) {
    issues.push(createImportRowIssue({
      previewRow: args.previewRow,
      targetField: 'recordedAt',
      code: 'mortality-recorded-at-required',
      severity: 'error',
      message: 'Recorded at is required.'
    }))
  } else {
    const normalizedRecordedAt = normalizeToIsoZ(rawRecordedAt)
    if (!normalizedRecordedAt.ok) {
      issues.push(createImportRowIssue({
        previewRow: args.previewRow,
        targetField: 'recordedAt',
        code: 'mortality-recorded-at-invalid',
        severity: 'error',
        message: 'Recorded at must be a recognizable date or date-time.'
      }))
    }
  }

  if (!rawMortalityCount) {
    issues.push(createImportRowIssue({
      previewRow: args.previewRow,
      targetField: 'mortalityCount',
      code: 'mortality-count-required',
      severity: 'error',
      message: 'Mortality count is required.'
    }))
  } else {
    const parsedCount = Number.parseFloat(rawMortalityCount.replaceAll(',', '.'))
    if (!Number.isFinite(parsedCount)) {
      issues.push(createImportRowIssue({
        previewRow: args.previewRow,
        targetField: 'mortalityCount',
        code: 'mortality-count-invalid',
        severity: 'error',
        message: 'Mortality count must be a recognizable number.'
      }))
    } else if (!Number.isInteger(parsedCount)) {
      issues.push(createImportRowIssue({
        previewRow: args.previewRow,
        targetField: 'mortalityCount',
        code: 'mortality-count-whole-number',
        severity: 'error',
        message: 'Mortality count must be a whole number.'
      }))
    } else if (parsedCount < 0) {
      issues.push(createImportRowIssue({
        previewRow: args.previewRow,
        targetField: 'mortalityCount',
        code: 'mortality-count-non-negative',
        severity: 'error',
        message: 'Mortality count must be zero or greater.'
      }))
    }
  }

  return issues
}

const buildMortalityMappedHeaders = (table: ParsedImportTable): MortalityMappedHeader[] => {
  return mapImportHeaders({
    headers: table.headers,
    normalizeHeader: normalizeMortalityImportHeader,
    targetByNormalizedHeader: MORTALITY_IMPORT_FIELD_MAP
  })
}

const buildMortalityImportArtifactsFromCsvText = (args: {
  text: string
  existingRows: MortalityImportRecord[]
  availableLots: MortalityImportLot[]
  lotKeyById: Map<string, string>
  assignedContext?: MortalityImportContext | null
  /**
   * Farm (or browser) timezone used to derive the `(lot, day)` dedup key for
   * the `mortality-duplicate-lot-day` warning — mirrors v2.1 #47/#41. Without
   * it, the legacy UTC-prefix day-key behavior is used.
   */
  timeZone?: string
}): {
  table: ParsedImportTable
  mappedHeaders: MortalityMappedHeader[]
  results: MortalityImportRowResult[]
} | null => {
  const parsedTable = parseCsvImportTable(args.text)
  if (!parsedTable) return null

  const mappedHeaders = buildMortalityMappedHeaders(parsedTable)
  const previewRows = buildImportPreviewRows({
    table: parsedTable,
    mappedHeaders,
    normalizeCellValue: (value) => value.trim()
  })
  const existingIds = new Set(args.existingRows.map((row) => toStableImportIdString(row.id)))
  const importedIds = new Set<string>()
  const dayKeyOptions = args.timeZone ? { timeZone: args.timeZone } : undefined
  // Preventive (lot, day) dedup, same shape as v2.1 #41 (production) / #42
  // (water): a CSV with no stable id column produces a fresh id on every
  // import run, so re-uploading the same file silently creates duplicate
  // MortalityLog rows with nothing at commit time telling the operator.
  // Index existing rows by `(dayKey, lotToken)` and warn — not block — when
  // an imported row collides with an existing row or another row in this
  // batch.
  const existingLotDayKeys = new Set<string>()
  for (const row of args.existingRows) {
    if (!row) continue
    const lotToken = resolveCanonicalLotKey(row.lotId, row.lot)
    if (lotToken === 'unknown-lot') continue
    const dayKey = toDayKeyDateOnly(row.recordedAt ?? '', dayKeyOptions)
    if (!dayKey) continue
    existingLotDayKeys.add(`${dayKey}::${lotToken}`)
  }
  const importedLotDayKeys = new Set<string>()

  const results = buildImportValidationResults({
    previewRows,
    buildRecord: (previewRow) => buildImportPreviewRecord({ previewRow }),
    validateRecord: ({ previewRow, record }) => validateMortalityImportRecord({ previewRow, record })
  }).map((validationResult) => {
    const row = validationResult.record
    const dedupeResult = ensureUniqueImportedRowId({
      row,
      existingIds,
      importedIds,
      idPrefix: 'mort'
    })
    const dedupeIssue = createImportDedupeIssue({
      previewRow: validationResult.previewRow,
      targetField: 'id',
      dedupeResult
    })

    const normalizedRecordedAt = normalizeToIsoZ(trimOptionalString(row.recordedAt) ?? '')
    if (normalizedRecordedAt.ok) {
      row.recordedAt = normalizedRecordedAt.isoZ
    }

    const normalizedLot = resolveImportedMortalityLot({
      row,
      availableLots: args.availableLots,
      lotKeyById: args.lotKeyById
    })
    // Charter D-1: MortalityLogService rejects a create with no lotId. A row
    // that only resolved a free-text lot label (no matching Lot) would
    // otherwise save locally as "ready" and then fail permanently at sync
    // time with no surfaced error — flag it here instead, at import time.
    const lotIdIssue = !normalizedLot.lotId
      ? createImportRowIssue({
        previewRow: validationResult.previewRow,
        targetField: 'lotId',
        code: 'mortality-lot-id-required',
        severity: 'error',
        message: normalizedLot.lotKey
          ? `Lot "${normalizedLot.lotKey}" did not match an existing lot. Select a matching lot before saving.`
          : 'A matching lot is required.'
      })
      : null
    const normalizedMortalityCount = normalizeImportedMortalityCountString(row.mortalityCount)
    const farmId = trimOptionalString(args.assignedContext?.farmId) ?? trimOptionalString(row.farmId) ?? ''
    const productionUnitId = trimOptionalString(args.assignedContext?.productionUnitId)
      ?? trimOptionalString(row.productionUnitId)
      ?? ''
    const notes = trimOptionalString(row.notes)

    const typedRow: MortalityImportRecord = {
      id: toStableImportIdString(row.id),
      farmId,
      productionUnitId,
      recordedAt: typeof row.recordedAt === 'string' ? row.recordedAt : '',
      mortalityCount: normalizedMortalityCount ?? '',
      ...(normalizedLot.lotKey ? { lot: normalizedLot.lotKey } : {}),
      ...(normalizedLot.lotId ? { lotId: normalizedLot.lotId } : {}),
      ...(notes ? { notes } : {})
    }

    // Preventive (lot, day) duplicate warning — same-batch or already-saved
    // collision. Skipped when lotId couldn't resolve (lotIdIssue already
    // covers that row) since `unknown-lot` would otherwise bucket every
    // unresolved row together.
    const lotToken = resolveCanonicalLotKey(typedRow.lotId, typedRow.lot)
    const dayKey = toDayKeyDateOnly(typedRow.recordedAt, dayKeyOptions)
    let dupIssue: ReturnType<typeof createImportRowIssue<MortalityImportField>> | null = null
    if (lotToken !== 'unknown-lot' && dayKey) {
      const lotDayKey = `${dayKey}::${lotToken}`
      const collidesWithExisting = existingLotDayKeys.has(lotDayKey)
      const collidesWithImported = importedLotDayKeys.has(lotDayKey)
      if (collidesWithExisting || collidesWithImported) {
        dupIssue = createImportRowIssue({
          previewRow: validationResult.previewRow,
          targetField: 'recordedAt',
          code: 'mortality-duplicate-lot-day',
          severity: 'warning',
          message: collidesWithExisting
            ? 'A mortality log for this lot and day already exists. The import will create a duplicate — review before saving.'
            : 'Another imported row already targets this lot and day. The import will create a duplicate — review before saving.'
        })
      }
      importedLotDayKeys.add(lotDayKey)
    }

    const extraIssues = [dedupeIssue, lotIdIssue, dupIssue].filter((issue) => issue !== null)

    return {
      ...validationResult,
      record: typedRow,
      issues: extraIssues.length > 0 ? [...validationResult.issues, ...extraIssues] : validationResult.issues,
      dedupeResult
    }
  })

  return {
    table: parsedTable,
    mappedHeaders,
    results
  }
}

export const buildImportedMortalityRowsFromCsvText = (args: {
  text: string
  existingRows: MortalityImportRecord[]
  availableLots: MortalityImportLot[]
  lotKeyById: Map<string, string>
  assignedContext?: MortalityImportContext | null
  timeZone?: string
}): MortalityImportRecord[] => {
  return buildImportedMortalityResultsFromCsvText(args).map((result) => result.record)
}

export const buildImportedMortalityResultsFromCsvText = (args: {
  text: string
  existingRows: MortalityImportRecord[]
  availableLots: MortalityImportLot[]
  lotKeyById: Map<string, string>
  assignedContext?: MortalityImportContext | null
  timeZone?: string
}): MortalityImportRowResult[] => {
  return buildMortalityImportArtifactsFromCsvText(args)?.results ?? []
}

export const buildMortalityImportSessionFromCsvText = (args: {
  text: string
  existingRows: MortalityImportRecord[]
  availableLots: MortalityImportLot[]
  lotKeyById: Map<string, string>
  sourceFile?: ImportSourceFile | null
  assignedContext?: MortalityImportContext | null
  targetLabel?: string
  timeZone?: string
}): MortalityImportSession | null => {
  const artifacts = buildMortalityImportArtifactsFromCsvText(args)
  if (!artifacts) return null

  return buildImportSession({
    sourceFile: args.sourceFile ?? null,
    table: artifacts.table,
    selectedTarget: {
      targetKey: 'mortality',
      label: args.targetLabel ?? 'Mortality'
    },
    assignedContext: args.assignedContext ?? null,
    mappedHeaders: artifacts.mappedHeaders,
    results: artifacts.results
  })
}
